import { X, Trash2, Plus, Link2, Hash, FileText, ChevronRight, ImageIcon, ExternalLink, GripVertical } from 'lucide-react';
import { useState, useRef } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import type { LandingSection } from '@easyflow/shared';
import { SECTION_TYPE_LABELS } from './types';

// Il modello sezione è dinamico (jsonb generato dall'AI / editor): `props` è un
// blob libero. Tipi LARGHI e permissivi, MAI contratti stretti: l'elemento
// generico di una collezione (items/members/steps/links/plans) è un record di
// valori scalari o annidati; `SectionProps` è il record dei props con le
// collezioni note tipizzate loosely + index signature per il resto.
type PropValue = string | number | boolean | null | undefined | SectionItem[];
interface SectionItem {
  [key: string]: PropValue;
}
interface SectionProps {
  items?: SectionItem[];
  members?: SectionItem[];
  steps?: SectionItem[];
  links?: SectionItem[];
  plans?: SectionItem[];
  [key: string]: PropValue;
}

// Coercizioni al boundary: i valori jsonb sono `unknown` a livello di tipo, ma
// la UI si aspetta stringhe/numeri per il binding dei controlli.
const asStr = (v: PropValue): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
const asStrOrNum = (v: PropValue): string | number =>
  typeof v === 'string' || typeof v === 'number' ? v : '';

interface LinkablePage {
  id: string;
  slug: string;
  title: string;
  status: string;
  parent_id?: string | null;
  sectionAnchors?: { id: string; type: string; label: string }[];
}

interface PropertiesPanelProps {
  section: LandingSection | null;
  onUpdate: (sectionId: string, newProps: SectionProps) => void;
  onClose: () => void;
  linkablePages?: LinkablePage[];
  currentPageId?: string;
  currentSections?: LandingSection[];
  onOpenStockImage?: (sectionId: string) => void;
  appointmentForms?: Array<{ id: string; name: string; slug: string }>;
}

const NAVBAR_STYLE_OPTIONS = [
  { value: 'standard', label: 'Standard (Logo + Link + CTA)' },
  { value: 'minimal', label: 'Minimal (Logo + Link)' },
  { value: 'centered', label: 'Centrata' },
  { value: 'transparent', label: 'Trasparente (overlay su Hero)' },
];

const TEXT_FIELDS: Record<string, { label: string; field: string; multiline?: boolean }[]> = {
  navbar: [
    { label: 'Logo (testo)', field: 'logoText' },
    { label: 'Logo (URL immagine)', field: 'logoImage' },
    { label: 'Testo CTA', field: 'ctaText' },
    { label: 'Link CTA', field: 'ctaLink' },
    { label: 'Colore sfondo', field: 'bgColor' },
  ],
  hero: [
    { label: 'Badge', field: 'badge' },
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
    { label: 'Testo CTA', field: 'ctaText' },
    { label: 'Link CTA', field: 'ctaLink' },
    { label: 'CTA Secondario', field: 'secondaryCtaText' },
    { label: 'Link CTA Secondario', field: 'secondaryCtaLink' },
    { label: 'Immagine sfondo', field: 'backgroundImage' },
  ],
  text: [
    { label: 'Etichetta', field: 'label' },
    { label: 'Titolo', field: 'title' },
    { label: 'Contenuto', field: 'content', multiline: true },
  ],
  cta: [
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
    { label: 'Testo pulsante', field: 'buttonText' },
    { label: 'Link pulsante', field: 'buttonLink' },
  ],
  contact: [
    { label: 'Etichetta', field: 'label' },
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
    { label: 'Email', field: 'email' },
    { label: 'Telefono', field: 'phone' },
    { label: 'Indirizzo', field: 'address' },
  ],
  stats: [
    { label: 'Titolo', field: 'title' },
  ],
  gallery: [
    { label: 'Titolo', field: 'title' },
  ],
  logos: [
    { label: 'Titolo', field: 'title' },
  ],
  features: [
    { label: 'Etichetta', field: 'label' },
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
  ],
  testimonials: [
    { label: 'Etichetta', field: 'label' },
    { label: 'Titolo', field: 'title' },
  ],
  pricing: [
    { label: 'Etichetta', field: 'label' },
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
  ],
  faq: [
    { label: 'Etichetta', field: 'label' },
    { label: 'Titolo', field: 'title' },
  ],
  task_form_embed: [
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
  ],
  appointment_form_embed: [
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
  ],
  form_embed: [
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
  ],
  whatsapp_button: [
    { label: 'Numero WhatsApp', field: 'phoneNumber' },
    { label: 'Messaggio predefinito', field: 'message' },
    { label: 'Testo pulsante', field: 'buttonText' },
    { label: 'Posizione', field: 'position' },
  ],
  video: [
    { label: 'Etichetta', field: 'label' },
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
    { label: 'URL Video (YouTube/Vimeo)', field: 'videoUrl' },
    { label: 'Aspect Ratio (es. 16:9)', field: 'aspectRatio' },
  ],
  countdown: [
    { label: 'Titolo', field: 'title' },
    { label: 'Sottotitolo', field: 'subtitle' },
    { label: 'Data Target (ISO)', field: 'targetDate' },
    { label: 'Messaggio Scaduto', field: 'expiredMessage' },
  ],
};

const ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Sinistra' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Destra' },
];

const LAYOUT_OPTIONS = [
  { value: 'grid', label: 'Griglia' },
  { value: 'bento', label: 'Bento' },
  { value: 'alternating', label: 'Alternato' },
];

const LINK_FIELDS = new Set(['ctaLink', 'secondaryCtaLink', 'buttonLink', 'navCtaLink']);
const IMAGE_FIELDS = new Set(['backgroundImage']);

function LinkEditorModal({ open, onOpenChange, value, onChange, siblingPages, currentSections, currentPageId }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  siblingPages: LinkablePage[];
  currentSections: LandingSection[];
  currentPageId?: string;
}) {
  const anchors = currentSections
    .filter((s): s is LandingSection & { id: string; type: string } => Boolean(s.id && s.type))
    .map(s => {
      const title = (s.props as SectionProps | undefined)?.title;
      return {
        value: `#${s.id}`,
        label: (typeof title === 'string' && title) || SECTION_TYPE_LABELS[s.type] || s.type,
        type: s.type,
      };
    });

  const currentPage = currentPageId ? siblingPages.find(sp => sp.id === currentPageId) : null;
  const childPages = currentPage ? siblingPages.filter(sp => sp.parent_id === currentPageId) : [];
  const otherPages = siblingPages.filter(sp => sp.id !== currentPageId && sp.parent_id !== currentPageId);

  const handleSelect = (v: string) => {
    onChange(v);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Link2 className="h-4 w-4 text-indigo-500" />
            Seleziona destinazione link
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto">
          <div>
            <Label className="text-xs text-gray-500 mb-1">Link manuale</Label>
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://... oppure #sezione oppure /slug"
              className="text-xs h-8"
            />
          </div>

          {anchors.length > 0 && (
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-1.5">
                <Hash className="h-3 w-3" /> Ancore (questa pagina)
              </Label>
              <div className="space-y-1 max-h-[150px] overflow-y-auto">
                {anchors.map(a => (
                  <button
                    key={a.value}
                    onClick={() => handleSelect(a.value)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-center justify-between ${
                      value === a.value
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-indigo-300 hover:bg-indigo-50/50'
                    }`}
                  >
                    <span className="font-medium truncate">{a.label}</span>
                    <span className="text-gray-400 ml-2 shrink-0">{a.value}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {childPages.length > 0 && (
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-1.5">
                <ChevronRight className="h-3 w-3" /> Sottopagine
              </Label>
              <div className="space-y-1">
                {childPages.map(sp => (
                  <button
                    key={sp.id}
                    onClick={() => handleSelect(`/${sp.slug}`)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-center justify-between ${
                      value === `/${sp.slug}`
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-emerald-300 hover:bg-emerald-50/50'
                    }`}
                  >
                    <span className="font-medium truncate">{sp.title}</span>
                    <span className="text-gray-400 ml-2 shrink-0">/{sp.slug}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {otherPages.length > 0 && (
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-1.5">
                <FileText className="h-3 w-3" /> Altre pagine del progetto
              </Label>
              <div className="space-y-1 max-h-[150px] overflow-y-auto">
                {otherPages.map(sp => (
                  <button
                    key={sp.id}
                    onClick={() => handleSelect(`/${sp.slug}`)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-center justify-between ${
                      value === `/${sp.slug}`
                        ? 'bg-sky-50 dark:bg-sky-900/20 border-sky-300 dark:border-sky-600 text-sky-700 dark:text-sky-300'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-sky-300 hover:bg-sky-50/50'
                    }`}
                  >
                    <span className="font-medium truncate">{sp.title}</span>
                    <span className="text-gray-400 ml-2 shrink-0">/{sp.slug}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {anchors.length === 0 && childPages.length === 0 && otherPages.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-4">Nessuna pagina o ancora disponibile. Inserisci un link manuale.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getLinkDisplayLabel(value: string, siblingPages: LinkablePage[], currentSections: LandingSection[]): string | null {
  if (!value) return null;
  if (value.startsWith('#')) {
    const section = currentSections.find(s => `#${s.id}` === value);
    if (section) {
      const title = (section.props as SectionProps | undefined)?.title;
      return (typeof title === 'string' && title) || SECTION_TYPE_LABELS[section.type ?? ''] || section.type || null;
    }
  }
  const page = siblingPages.find(sp => `/${sp.slug}` === value);
  if (page) return page.title;
  return null;
}

function SortableNavLink({ linkId, link, idx, onRemove, onLabelChange, onOpenLinkPicker }: {
  linkId: string;
  link: { label: string; url: string };
  idx: number;
  onRemove: () => void;
  onLabelChange: (val: string) => void;
  onOpenLinkPicker: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: linkId });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : undefined };
  return (
    <div ref={setNodeRef} style={style} className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-1.5 relative">
      <div className="absolute top-1 right-1 flex items-center gap-0.5">
        <button {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 transition-colors touch-none" title="Trascina per riordinare">
          <GripVertical className="h-3 w-3" />
        </button>
        <button onClick={onRemove} className="text-gray-300 hover:text-red-500 transition-colors" title="Rimuovi">
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      <Input
        value={link.label || ''}
        onChange={(e) => onLabelChange(e.target.value)}
        placeholder="Testo link"
        className="text-xs h-7 pr-14"
      />
      <button
        onClick={onOpenLinkPicker}
        className={`w-full text-left px-2 py-1.5 rounded border text-xs flex items-center gap-2 ${
          link.url && link.url !== '#'
            ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800'
            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 border-dashed'
        }`}
      >
        <Link2 className={`h-3 w-3 shrink-0 ${link.url && link.url !== '#' ? 'text-indigo-500' : 'text-gray-400'}`} />
        <span className="truncate flex-1">{link.url || 'Seleziona...'}</span>
      </button>
    </div>
  );
}

export default function PropertiesPanel({ section, onUpdate, onClose, linkablePages = [], currentPageId, currentSections = [], onOpenStockImage, appointmentForms = [] }: PropertiesPanelProps) {
  const [linkEditorField, setLinkEditorField] = useState<string | null>(null);
  const [linkEditorValue, setLinkEditorValue] = useState('');
  const [planLinkEditor, setPlanLinkEditor] = useState<{ planIdx: number; field: string } | null>(null);
  const navbarAutoPopulatedRef = useRef<Set<string>>(new Set());
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));

  if (!section) return null;

  const type = section.type ?? '';
  const id = section.id ?? '';
  const props = (section.props ?? {}) as SectionProps;
  const fields = TEXT_FIELDS[type] || [];
  const typeLabel = SECTION_TYPE_LABELS[type] || type;
  const siblingPages = linkablePages.filter(lp => lp.id !== currentPageId);

  const autoPopKey = `${currentPageId || ''}:${id}`;
  if (type === 'navbar' && (!props.links || props.links.length === 0) && siblingPages.length > 0 && !navbarAutoPopulatedRef.current.has(autoPopKey)) {
    navbarAutoPopulatedRef.current.add(autoPopKey);
    const autoLinks: SectionItem[] = siblingPages.map(sp => ({ label: sp.title, url: `/${sp.slug}` }));
    setTimeout(() => onUpdate(id, { ...props, links: autoLinks }), 0);
  }

  const updateField = (field: string, value: PropValue) => {
    onUpdate(id, { ...props, [field]: value });
  };

  const updateItemField = (index: number, field: string, value: PropValue) => {
    const items = [...(props.items || [])];
    items[index] = { ...items[index], [field]: value };
    onUpdate(id, { ...props, items });
  };

  const removeItem = (index: number) => {
    const items = (props.items || []).filter((_, i) => i !== index);
    onUpdate(id, { ...props, items });
  };

  const addItem = () => {
    const items = [...(props.items || [])];
    if (type === 'features') items.push({ title: 'Nuova feature', description: 'Descrizione', icon: 'Star' });
    else if (type === 'testimonials') items.push({ name: 'Nome', role: 'Ruolo', text: 'Testo recensione', rating: 5 });
    else if (type === 'faq') items.push({ question: 'Nuova domanda', answer: 'Risposta' });
    else if (type === 'stats') items.push({ value: '100', label: 'Nuova statistica' });
    else items.push({});
    onUpdate(id, { ...props, items });
  };

  const openLinkEditor = (field: string) => {
    setLinkEditorField(field);
    setLinkEditorValue(asStr(props[field]));
  };

  const openPlanLinkEditor = (planIdx: number, field: string) => {
    setPlanLinkEditor({ planIdx, field });
    setLinkEditorValue(asStr(props.plans?.[planIdx]?.[field]));
  };

  return (
    <div className="w-[300px] min-w-[300px] border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{typeLabel}</p>
          <p className="text-[10px] text-gray-500">{id}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {type === 'appointment_form_embed' && (
          <>
            <div>
              <Label className="text-xs font-medium mb-1.5">Form Appuntamento</Label>
              <Select
                value={asStr(props.formSlug) || asStr(props.form_slug)}
                onValueChange={(v) => onUpdate(id, { ...props, formSlug: v, form_slug: v })}
              >
                <SelectTrigger className="text-xs h-8">
                  <SelectValue placeholder={appointmentForms.length ? 'Seleziona un form…' : 'Nessun form disponibile'} />
                </SelectTrigger>
                <SelectContent>
                  {appointmentForms.map(f => (
                    <SelectItem key={f.id} value={f.slug}>{f.name} <span className="text-gray-400">/{f.slug}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!appointmentForms.length && (
                <p className="text-[10px] text-gray-500 mt-1">Crea form pubblici da Calendario → Form Pubblici</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium mb-1.5">Larghezza</Label>
                <Input className="text-xs h-8" placeholder="100% o 600px" value={asStr(props.width) || '100%'} onChange={(e) => updateField('width', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1.5">Altezza</Label>
                <Input className="text-xs h-8" placeholder="auto o 800px" value={asStr(props.height) || 'auto'} onChange={(e) => updateField('height', e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5">Tema (override)</Label>
              <Select value={asStr(props.themeOverride)} onValueChange={(v) => updateField('themeOverride', v === '_none' ? '' : v)}>
                <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Auto (eredita pagina)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Auto (eredita pagina)</SelectItem>
                  <SelectItem value="light">Chiaro</SelectItem>
                  <SelectItem value="dark">Scuro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {(type === 'hero' || type === 'text') && (
          <div>
            <Label className="text-xs font-medium mb-1.5">Allineamento</Label>
            <Select value={asStr(props.alignment) || 'left'} onValueChange={(v) => updateField('alignment', v)}>
              <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ALIGNMENT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {type === 'features' && (
          <div>
            <Label className="text-xs font-medium mb-1.5">Layout</Label>
            <Select value={asStr(props.layout) || 'grid'} onValueChange={(v) => updateField('layout', v)}>
              <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LAYOUT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {type === 'navbar' && (
          <>
            <div>
              <Label className="text-xs font-medium mb-1.5">Stile Navbar</Label>
              <Select value={asStr(props.style) || 'standard'} onValueChange={(v) => updateField('style', v)}>
                <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NAVBAR_STYLE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={props.sticky !== false}
                onChange={(e) => updateField('sticky', e.target.checked)}
                className="rounded border-gray-300"
                id="navbar-sticky"
              />
              <Label htmlFor="navbar-sticky" className="text-xs font-medium cursor-pointer">Sticky (fissa in alto)</Label>
            </div>
          </>
        )}

        {fields.map(f => (
          <div key={f.field}>
            <Label className="text-xs font-medium mb-1.5">{f.label}</Label>
            {f.multiline ? (
              <textarea
                value={asStr(props[f.field])}
                onChange={(e) => updateField(f.field, e.target.value)}
                className="w-full text-xs p-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-y min-h-[80px]"
                rows={4}
              />
            ) : IMAGE_FIELDS.has(f.field) ? (
              <div className="space-y-1.5">
                {props[f.field] && (
                  <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 h-20">
                    <img
                      src={asStr(props[f.field])}
                      alt="Anteprima"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
                <div className="flex gap-1.5">
                  <Input
                    value={asStr(props[f.field])}
                    onChange={(e) => updateField(f.field, e.target.value)}
                    placeholder="URL immagine..."
                    className="text-xs h-8 flex-1"
                  />
                  {onOpenStockImage && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 shrink-0"
                      onClick={() => onOpenStockImage(id)}
                      title="Cerca immagine stock"
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {props[f.field] && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => updateField(f.field, '')}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Rimuovi immagine
                  </Button>
                )}
              </div>
            ) : LINK_FIELDS.has(f.field) ? (
              <div className="space-y-1">
                <button
                  onClick={() => openLinkEditor(f.field)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-center gap-2 group ${
                    props[f.field]
                      ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800 hover:border-indigo-400'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 border-dashed hover:border-indigo-300'
                  }`}
                >
                  <Link2 className={`h-3.5 w-3.5 shrink-0 ${props[f.field] ? 'text-indigo-500' : 'text-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    {props[f.field] ? (
                      <>
                        <p className="font-medium text-gray-900 dark:text-white truncate">{asStr(props[f.field])}</p>
                        {(() => {
                          const label = getLinkDisplayLabel(asStr(props[f.field]), linkablePages, currentSections);
                          return label ? <p className="text-[10px] text-gray-500 truncate">{label}</p> : null;
                        })()}
                      </>
                    ) : (
                      <p className="text-gray-400 italic">Clicca per selezionare...</p>
                    )}
                  </div>
                  <ExternalLink className="h-3 w-3 text-gray-300 group-hover:text-indigo-400 shrink-0" />
                </button>
              </div>
            ) : (
              <Input
                value={asStr(props[f.field])}
                onChange={(e) => updateField(f.field, e.target.value)}
                className="text-xs h-8"
              />
            )}
          </div>
        ))}

        {type === 'gallery' && (
          <div>
            <Label className="text-xs font-medium mb-1.5">Colonne</Label>
            <Select value={String(props.columns || 3)} onValueChange={(v) => updateField('columns', parseInt(v))}>
              <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="4">4</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {(type === 'features' || type === 'testimonials' || type === 'faq' || type === 'stats' || type === 'logos' || type === 'gallery') && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-medium">
                {type === 'features' ? 'Feature' : type === 'testimonials' ? 'Recensioni' : type === 'faq' ? 'Domande' : type === 'stats' ? 'Statistiche' : type === 'logos' ? 'Loghi' : 'Immagini'}
                <span className="text-gray-400 ml-1">({(props.items || []).length})</span>
              </Label>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={addItem}>
                <Plus className="h-3 w-3 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {(props.items || []).map((item: SectionItem, idx: number) => (
                <div key={idx} className="p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-1.5 relative">
                  <button onClick={() => removeItem(idx)} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-500 transition-colors" title="Rimuovi">
                    <Trash2 className="h-3 w-3" />
                  </button>
                  {type === 'features' && (
                    <>
                      <Input value={asStr(item.icon)} onChange={(e) => updateItemField(idx, 'icon', e.target.value)} placeholder="Icona (es: Star)" className="text-xs h-7" />
                      <Input value={asStr(item.title)} onChange={(e) => updateItemField(idx, 'title', e.target.value)} placeholder="Titolo" className="text-xs h-7" />
                      <Input value={asStr(item.description)} onChange={(e) => updateItemField(idx, 'description', e.target.value)} placeholder="Descrizione" className="text-xs h-7" />
                    </>
                  )}
                  {type === 'testimonials' && (
                    <>
                      <Input value={asStr(item.name)} onChange={(e) => updateItemField(idx, 'name', e.target.value)} placeholder="Nome" className="text-xs h-7" />
                      <Input value={asStr(item.role)} onChange={(e) => updateItemField(idx, 'role', e.target.value)} placeholder="Ruolo" className="text-xs h-7" />
                      <textarea
                        value={asStr(item.text)}
                        onChange={(e) => updateItemField(idx, 'text', e.target.value)}
                        placeholder="Recensione"
                        className="w-full text-xs p-1.5 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-y min-h-[40px]"
                        rows={2}
                      />
                      <Select value={String(asStrOrNum(item.rating) || 5)} onValueChange={(v) => updateItemField(idx, 'rating', parseInt(v))}>
                        <SelectTrigger className="text-xs h-7"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5].map(r => <SelectItem key={r} value={String(r)}>{'★'.repeat(r)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {type === 'faq' && (
                    <>
                      <Input value={asStr(item.question)} onChange={(e) => updateItemField(idx, 'question', e.target.value)} placeholder="Domanda" className="text-xs h-7" />
                      <textarea
                        value={asStr(item.answer)}
                        onChange={(e) => updateItemField(idx, 'answer', e.target.value)}
                        placeholder="Risposta"
                        className="w-full text-xs p-1.5 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-y min-h-[40px]"
                        rows={2}
                      />
                    </>
                  )}
                  {type === 'stats' && (
                    <>
                      <Input value={asStr(item.value)} onChange={(e) => updateItemField(idx, 'value', e.target.value)} placeholder="Valore" className="text-xs h-7" />
                      <Input value={asStr(item.label)} onChange={(e) => updateItemField(idx, 'label', e.target.value)} placeholder="Etichetta" className="text-xs h-7" />
                    </>
                  )}
                  {type === 'logos' && (
                    <>
                      <Input value={asStr(item.name)} onChange={(e) => updateItemField(idx, 'name', e.target.value)} placeholder="Nome azienda" className="text-xs h-7" />
                      <Input value={asStr(item.url)} onChange={(e) => updateItemField(idx, 'url', e.target.value)} placeholder="URL logo" className="text-xs h-7" />
                    </>
                  )}
                  {type === 'gallery' && (
                    <>
                      <Input value={asStr(item.url)} onChange={(e) => updateItemField(idx, 'url', e.target.value)} placeholder="URL immagine" className="text-xs h-7" />
                      <Input value={asStr(item.alt)} onChange={(e) => updateItemField(idx, 'alt', e.target.value)} placeholder="Testo alternativo" className="text-xs h-7" />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {type === 'team' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-medium">
                Membri <span className="text-gray-400 ml-1">({(props.members || []).length})</span>
              </Label>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => {
                const members = [...(props.members || []), { name: 'Nuovo Membro', role: 'Ruolo', bio: '', image: '' }];
                onUpdate(id, { ...props, members });
              }}>
                <Plus className="h-3 w-3 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {(props.members || []).map((m: SectionItem, idx: number) => (
                <div key={idx} className="p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-1.5 relative">
                  <button onClick={() => {
                    const members = [...(props.members || [])];
                    members.splice(idx, 1);
                    onUpdate(id, { ...props, members });
                  }} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-500 transition-colors" title="Rimuovi">
                    <Trash2 className="h-3 w-3" />
                  </button>
                  <Input value={asStr(m.name)} onChange={(e) => {
                    const members = [...(props.members || [])];
                    members[idx] = { ...members[idx], name: e.target.value };
                    onUpdate(id, { ...props, members });
                  }} placeholder="Nome" className="text-xs h-7" />
                  <Input value={asStr(m.role)} onChange={(e) => {
                    const members = [...(props.members || [])];
                    members[idx] = { ...members[idx], role: e.target.value };
                    onUpdate(id, { ...props, members });
                  }} placeholder="Ruolo" className="text-xs h-7" />
                  <Input value={asStr(m.bio)} onChange={(e) => {
                    const members = [...(props.members || [])];
                    members[idx] = { ...members[idx], bio: e.target.value };
                    onUpdate(id, { ...props, members });
                  }} placeholder="Bio" className="text-xs h-7" />
                  <Input value={asStr(m.image)} onChange={(e) => {
                    const members = [...(props.members || [])];
                    members[idx] = { ...members[idx], image: e.target.value };
                    onUpdate(id, { ...props, members });
                  }} placeholder="URL Immagine" className="text-xs h-7" />
                </div>
              ))}
            </div>
          </div>
        )}

        {type === 'timeline' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-medium">
                Step <span className="text-gray-400 ml-1">({(props.steps || []).length})</span>
              </Label>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => {
                const steps = [...(props.steps || []), { title: 'Nuovo Step', description: 'Descrizione', icon: '' }];
                onUpdate(id, { ...props, steps });
              }}>
                <Plus className="h-3 w-3 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {(props.steps || []).map((step: SectionItem, idx: number) => (
                <div key={idx} className="p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-1.5 relative">
                  <button onClick={() => {
                    const steps = [...(props.steps || [])];
                    steps.splice(idx, 1);
                    onUpdate(id, { ...props, steps });
                  }} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-500 transition-colors" title="Rimuovi">
                    <Trash2 className="h-3 w-3" />
                  </button>
                  <Input value={asStr(step.icon)} onChange={(e) => {
                    const steps = [...(props.steps || [])];
                    steps[idx] = { ...steps[idx], icon: e.target.value };
                    onUpdate(id, { ...props, steps });
                  }} placeholder="Icona (es: Star)" className="text-xs h-7" />
                  <Input value={asStr(step.title)} onChange={(e) => {
                    const steps = [...(props.steps || [])];
                    steps[idx] = { ...steps[idx], title: e.target.value };
                    onUpdate(id, { ...props, steps });
                  }} placeholder="Titolo" className="text-xs h-7" />
                  <Input value={asStr(step.description)} onChange={(e) => {
                    const steps = [...(props.steps || [])];
                    steps[idx] = { ...steps[idx], description: e.target.value };
                    onUpdate(id, { ...props, steps });
                  }} placeholder="Descrizione" className="text-xs h-7" />
                </div>
              ))}
            </div>
          </div>
        )}

        {type === 'contact' && props.address && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-medium">Mostra Mappa</Label>
              <input type="checkbox" checked={props.showMap !== false} onChange={(e) => onUpdate(id, { ...props, showMap: e.target.checked })} />
            </div>
            {props.showMap !== false && (
              <div>
                <Label className="text-xs">Zoom mappa</Label>
                <Input type="number" min={1} max={20} value={asStrOrNum(props.mapZoom) || 15} onChange={(e) => onUpdate(id, { ...props, mapZoom: parseInt(e.target.value) || 15 })} className="text-xs h-7" />
              </div>
            )}
          </div>
        )}

        {type === 'google_map' && (
          <div className="space-y-2">
            <div>
              <Label className="text-xs font-medium">Indirizzo / Luogo</Label>
              <Input value={asStr(props.address)} onChange={(e) => onUpdate(id, { ...props, address: e.target.value })} placeholder="Via Roma 1, Milano" className="text-xs h-7" />
            </div>
            <div>
              <Label className="text-xs">Zoom (1-20)</Label>
              <Input type="number" min={1} max={20} value={asStrOrNum(props.zoom) || 15} onChange={(e) => onUpdate(id, { ...props, zoom: parseInt(e.target.value) || 15 })} className="text-xs h-7" />
            </div>
            <div>
              <Label className="text-xs">Altezza mappa (px)</Label>
              <Input type="number" min={150} max={800} value={asStrOrNum(props.height) || 400} onChange={(e) => onUpdate(id, { ...props, height: parseInt(e.target.value) || 400 })} className="text-xs h-7" />
            </div>
          </div>
        )}

        {type === 'navbar' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-medium">
                Link navigazione
                <span className="text-gray-400 ml-1">({(props.links || []).length})</span>
              </Label>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => {
                const links = [...(props.links || []), { label: 'Nuovo link', url: '#' }];
                onUpdate(id, { ...props, links });
              }}>
                <Plus className="h-3 w-3 mr-1" /> Aggiungi
              </Button>
            </div>
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event: DragEndEvent) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const links = props.links || [];
                const oldIndex = links.findIndex((_, i) => `nav-link-${i}` === active.id);
                const newIndex = links.findIndex((_, i) => `nav-link-${i}` === over.id);
                if (oldIndex !== -1 && newIndex !== -1) {
                  onUpdate(id, { ...props, links: arrayMove([...links], oldIndex, newIndex) });
                }
              }}
            >
              <SortableContext items={(props.links || []).map((_, i) => `nav-link-${i}`)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {(props.links || []).map((link: SectionItem, idx: number) => (
                    <SortableNavLink
                      key={`nav-link-${idx}`}
                      linkId={`nav-link-${idx}`}
                      link={{ label: asStr(link.label), url: asStr(link.url) }}
                      idx={idx}
                      onRemove={() => {
                        const links = (props.links || []).filter((_, i) => i !== idx);
                        onUpdate(id, { ...props, links });
                      }}
                      onLabelChange={(val) => {
                        const links = [...(props.links || [])];
                        links[idx] = { ...links[idx], label: val };
                        onUpdate(id, { ...props, links });
                      }}
                      onOpenLinkPicker={() => {
                        setPlanLinkEditor({ planIdx: idx, field: 'url' });
                        setLinkEditorValue(asStr(link.url));
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {type === 'pricing' && props.plans && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-medium">
                Piani <span className="text-gray-400 ml-1">({props.plans.length})</span>
              </Label>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => {
                const plans = [...(props.plans || []), { name: 'Nuovo Piano', price: '€0', features: [], ctaText: 'Inizia', ctaLink: '#contact' }];
                onUpdate(id, { ...props, plans });
              }}>
                <Plus className="h-3 w-3 mr-1" /> Aggiungi
              </Button>
            </div>
            {(props.plans || []).map((plan: SectionItem, idx: number) => (
              <div key={idx} className="p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-1.5 mb-2">
                <span className="text-[10px] font-semibold text-gray-500 uppercase">Piano #{idx + 1}</span>
                <Input value={asStr(plan.name)} onChange={(e) => {
                  const plans = [...(props.plans || [])];
                  plans[idx] = { ...plans[idx], name: e.target.value };
                  onUpdate(id, { ...props, plans });
                }} placeholder="Nome" className="text-xs h-7" />
                <Input value={asStr(plan.price)} onChange={(e) => {
                  const plans = [...(props.plans || [])];
                  plans[idx] = { ...plans[idx], price: e.target.value };
                  onUpdate(id, { ...props, plans });
                }} placeholder="Prezzo" className="text-xs h-7" />
                <Input value={asStr(plan.description)} onChange={(e) => {
                  const plans = [...(props.plans || [])];
                  plans[idx] = { ...plans[idx], description: e.target.value };
                  onUpdate(id, { ...props, plans });
                }} placeholder="Descrizione" className="text-xs h-7" />
                <Input value={asStr(plan.ctaText)} onChange={(e) => {
                  const plans = [...(props.plans || [])];
                  plans[idx] = { ...plans[idx], ctaText: e.target.value };
                  onUpdate(id, { ...props, plans });
                }} placeholder="Testo CTA" className="text-xs h-7" />

                <Label className="text-xs font-medium mb-1">Link CTA</Label>
                <button
                  onClick={() => openPlanLinkEditor(idx, 'ctaLink')}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-center gap-2 group ${
                    plan.ctaLink
                      ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800 hover:border-indigo-400'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 border-dashed hover:border-indigo-300'
                  }`}
                >
                  <Link2 className={`h-3.5 w-3.5 shrink-0 ${plan.ctaLink ? 'text-indigo-500' : 'text-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    {plan.ctaLink ? (
                      <>
                        <p className="font-medium text-gray-900 dark:text-white truncate">{asStr(plan.ctaLink)}</p>
                        {(() => {
                          const label = getLinkDisplayLabel(asStr(plan.ctaLink), linkablePages, currentSections);
                          return label ? <p className="text-[10px] text-gray-500 truncate">{label}</p> : null;
                        })()}
                      </>
                    ) : (
                      <p className="text-gray-400 italic">Clicca per selezionare...</p>
                    )}
                  </div>
                  <ExternalLink className="h-3 w-3 text-gray-300 group-hover:text-indigo-400 shrink-0" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <LinkEditorModal
        open={linkEditorField !== null}
        onOpenChange={(open) => { if (!open) setLinkEditorField(null); }}
        value={linkEditorValue}
        onChange={(v) => {
          setLinkEditorValue(v);
          if (linkEditorField) updateField(linkEditorField, v);
        }}
        siblingPages={linkablePages}
        currentSections={currentSections}
        currentPageId={currentPageId}
      />

      <LinkEditorModal
        open={planLinkEditor !== null}
        onOpenChange={(open) => { if (!open) setPlanLinkEditor(null); }}
        value={linkEditorValue}
        onChange={(v) => {
          setLinkEditorValue(v);
          if (planLinkEditor) {
            if (type === 'navbar' && planLinkEditor.field === 'url') {
              const links = [...(props.links || [])];
              links[planLinkEditor.planIdx] = { ...links[planLinkEditor.planIdx], url: v };
              onUpdate(id, { ...props, links });
            } else {
              const plans = [...(props.plans || [])];
              plans[planLinkEditor.planIdx] = { ...plans[planLinkEditor.planIdx], [planLinkEditor.field]: v };
              onUpdate(id, { ...props, plans });
            }
          }
        }}
        siblingPages={linkablePages}
        currentSections={currentSections}
        currentPageId={currentPageId}
      />
    </div>
  );
}
